<section class="pay-dash">

  <header class="pay-dash__header">
    <span class="pay-dash__icon"><mat-icon>payments</mat-icon></span>
    <h1>Payments</h1>
  </header>

  <!-- Tabs -->
  <nav class="tabs" role="tablist">
    <button class="tabs__tab" role="tab" [class.is-active]="activeTab === 'RETURNS'"
            (click)="setTab('RETURNS')">RETURNS</button>
    <button class="tabs__tab" role="tab" [class.is-active]="activeTab === 'SCHEDULED'"
            (click)="setTab('SCHEDULED')">SCHEDULED</button>
    <button class="tabs__tab" role="tab" [class.is-active]="activeTab === 'RECEIPTS'"
            (click)="setTab('RECEIPTS')">RECEIPTS</button>
  </nav>

  <!-- Loading -->
  <div *ngIf="loading" class="state state--loading">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading payment information…</p>
  </div>

  <!-- Error -->
  <div *ngIf="error && !loading" class="state state--error">
    <mat-icon>error_outline</mat-icon>
    <p>{{ error }}</p>
    <button mat-flat-button color="primary" (click)="loadDashboard()"><mat-icon>refresh</mat-icon> Retry</button>
  </div>

  <ng-container *ngIf="!loading && !error && dashboard">

    <!-- ===================== RETURNS ===================== -->
    <ng-container *ngIf="activeTab === 'RETURNS'">

      <!-- Stat chips -->
      <div class="stats">
        <div class="stat">
          <span class="stat__num stat__num--pending">{{ monthsUnpaid }}</span>
          <span class="stat__label">Months<br>Unpaid</span>
        </div>
        <div class="stat">
          <span class="stat__num stat__num--pending">{{ returnsUnpaid }}</span>
          <span class="stat__label">Returns<br>Unpaid</span>
        </div>
        <div class="stat">
          <span class="stat__num stat__num--info">{{ partiallyPaid }}</span>
          <span class="stat__label">Partially<br>Paid</span>
        </div>
        <div class="stat">
          <span class="stat__num stat__num--danger">{{ pastDue }}</span>
          <span class="stat__label">Past<br>Due</span>
        </div>

        <span class="stats__spacer"></span>

        <button type="button" class="create-return-btn" (click)="createNextReturn()"
                [disabled]="creatingReturn" title="Create the next return for your account">
          <mat-icon>{{ creatingReturn ? 'hourglass_empty' : 'add' }}</mat-icon>
          <span>{{ creatingReturn ? 'Creating…' : 'Create next return' }}</span>
        </button>

        <!-- Mobile-only trigger: opens the filter bottom sheet -->
        <button type="button" class="pay-filter-trigger" (click)="openMobileFilters()">
          <mat-icon>tune</mat-icon>
          <span>Filters</span>
          <span class="pay-filter-badge" *ngIf="activeFilterCount">{{ activeFilterCount }}</span>
        </button>

        <!-- Filters: inline on desktop, bottom-sheet modal on mobile -->
        <div class="pay-filters" [class.is-open]="mobileFiltersOpen">
          <div class="pay-backdrop" (click)="closeMobileFilters()"></div>

          <div class="pay-sheet" role="dialog" aria-label="Filters">
            <div class="pay-sheet-header">
              <span>Filters</span>
              <button type="button" mat-icon-button (click)="closeMobileFilters()" aria-label="Close filters">
                <mat-icon>close</mat-icon>
              </button>
            </div>

            <mat-form-field class="filter-select" appearance="outline" subscriptSizing="dynamic">
              <mat-label>Years</mat-label>
              <mat-select [(value)]="filters.year" (selectionChange)="onFilterChange()">
                <mat-option value="ALL">All</mat-option>
                <mat-option *ngFor="let y of yearOptions" [value]="y">{{ y }}</mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field class="filter-select" appearance="outline" subscriptSizing="dynamic">
              <mat-label>Return type</mat-label>
              <mat-select [(value)]="filters.returnType" (selectionChange)="onFilterChange()">
                <mat-option value="ALL">All</mat-option>
                <mat-option *ngFor="let t of returnTypeOptions" [value]="t">{{ t }}</mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field class="filter-select" appearance="outline" subscriptSizing="dynamic">
              <mat-label>Status</mat-label>
              <mat-select [(value)]="filters.status" (selectionChange)="onFilterChange()">
                <mat-option value="ALL">All</mat-option>
                <mat-option *ngFor="let s of statusOptions" [value]="s">{{ s }}</mat-option>
              </mat-select>
            </mat-form-field>

            <button type="button" class="pay-sheet-done" (click)="closeMobileFilters()">Done</button>
          </div>
        </div>
      </div>

      <!-- Active filter chips -->
      <div class="filter-chips" *ngIf="hasActiveFilters">
        <span class="filter-chips__label"><mat-icon>filter_list</mat-icon> filters</span>
        <button class="chip" *ngIf="filters.year !== 'ALL'" (click)="clearFilter('year')">
          {{ filters.year }} <mat-icon>close</mat-icon>
        </button>
        <button class="chip" *ngIf="filters.returnType !== 'ALL'" (click)="clearFilter('returnType')">
          {{ filters.returnType }} <mat-icon>close</mat-icon>
        </button>
        <button class="chip" *ngIf="filters.status !== 'ALL'" (click)="clearFilter('status')">
          {{ filters.status }} <mat-icon>close</mat-icon>
        </button>
        <button class="chip chip--clear" (click)="clearAllFilters()">Clear all</button>
      </div>

      <!-- Table -->
      <div class="table-card">
        <div class="table-scroll">
          <table class="grid">
            <thead>
              <tr>
                <th>Period</th>
                <th>Return type</th>
                <th>Due date</th>
                <th>Properties</th>
                <th>Status</th>
                <th class="num">Total amount due</th>
                <th class="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let r of pagedReturns">
                <td data-label="Period"><span class="period">{{ r.period }}</span></td>
                <td data-label="Return type">{{ r.type }}
                  <span class="origin-tag" *ngIf="r.origin === 'SUPPLEMENTAL'"
                        title="Added for a property that joined after this period was already paid">Supplemental</span>
                  <span class="origin-tag" *ngIf="r.shared">Shared from {{ r.owningAccountName }}</span>
                </td>
                <td data-label="Due date">{{ r.dueDate | date: 'MM/dd/yyyy' }}</td>
                <td data-label="Properties">{{ r.propertyCount ?? 0 }}</td>
                <td data-label="Status">
                  <span class="status-pill" [ngClass]="getStatusClass(r.status)">
                    <span class="status-pill__dot"></span>{{ r.status }}
                  </span>
                </td>
                <td data-label="Total amount due" class="num amount">{{ r.balanceDue | currency }}</td>
                <td class="actions-col">
                  <button class="icon-btn icon-btn--view" (click)="viewReturn(r)" aria-label="Open / edit return" title="Open / edit return">
                    <mat-icon>edit</mat-icon>
                  </button>
                  <!-- Not [disabled]: a disabled button suppresses its own title tooltip and drops
                       out of the tab order, which hid the reason the row cannot be paid. addToBasket()
                       already no-ops for both blocked and already-in-cart returns. -->
                  <button class="icon-btn icon-btn--add" (click)="addToBasket(r.id)"
                          [class.icon-btn--blocked]="isInBasket(r.id) || isBlockedFromBasket(r)"
                          [attr.aria-disabled]="isInBasket(r.id) || isBlockedFromBasket(r)"
                          [title]="isInBasket(r.id) ? 'Already in cart' : blockedTooltip(r)"
                          [attr.aria-label]="isInBasket(r.id) ? 'Already in cart' : blockedTooltip(r)">
                    <mat-icon>{{ isBlockedFromBasket(r) && !isInBasket(r.id) ? (hasNothingDue(r) ? 'edit_note' : 'lock') : 'add' }}</mat-icon>
                  </button>
                </td>
              </tr>

              <tr *ngIf="pagedReturns.length === 0">
                <td colspan="7" class="empty-row">
                  <mat-icon>check_circle_outline</mat-icon>
                  <span>No returns match the current filters.</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="pager" *ngIf="filteredReturns.length > 0">
          <label class="pager__size">
            Rows per page:
            <select [ngModel]="pageSize" (ngModelChange)="onPageSizeChange($event)">
              <option *ngFor="let s of pageSizeOptions" [ngValue]="s">{{ s }}</option>
            </select>
          </label>
          <span class="pager__range">{{ rangeLabel }}</span>
          <button class="pager__btn" (click)="prevPage()" [disabled]="!canPrev" aria-label="Previous page">
            <mat-icon>chevron_left</mat-icon>
          </button>
          <button class="pager__btn" (click)="nextPage()" [disabled]="!canNext" aria-label="Next page">
            <mat-icon>chevron_right</mat-icon>
          </button>
        </div>
      </div>
    </ng-container>

    <!-- ===================== SCHEDULED ===================== -->
    <ng-container *ngIf="activeTab === 'SCHEDULED'">
      <div class="table-card">
        <div class="state state--empty" *ngIf="!scheduledLoading && scheduledPayments.length === 0">
          <mat-icon>event_available</mat-icon>
          <p class="state__title">No scheduled payments</p>
          <p class="state__hint">Payments you schedule for a future date will appear here. Add returns to your cart and choose "Schedule for later" at checkout.</p>
        </div>

        <div class="table-scroll" *ngIf="scheduledPayments.length > 0">
          <table class="grid">
            <thead>
              <tr>
                <th>Scheduled for</th>
                <th>Returns</th>
                <th>Payment method</th>
                <th>Status</th>
                <th class="num">Amount</th>
                <th class="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let sp of scheduledPayments">
                <td data-label="Scheduled for">{{ sp.scheduledDate | date: 'MM/dd/yyyy' }}</td>
                <td data-label="Returns">
                  <span *ngFor="let ret of sp.returns; let last = last">{{ ret.period }}<span *ngIf="!last">, </span></span>
                </td>
                <td data-label="Payment method">{{ sp.methodLabel }}</td>
                <td data-label="Status">
                  <span class="status-pill" [ngClass]="getStatusClass(sp.status)">
                    <span class="status-pill__dot"></span>{{ sp.status }}
                  </span>
                  <div class="state__hint" *ngIf="sp.status === 'FAILED' && sp.failureMessage">{{ sp.failureMessage }}</div>
                </td>
                <td data-label="Amount" class="num amount">{{ sp.amount | currency }}</td>
                <td class="actions-col">
                  <button class="icon-btn icon-btn--view" *ngIf="sp.status === 'PENDING'"
                          (click)="cancelScheduled(sp)" title="Cancel scheduled payment"
                          aria-label="Cancel scheduled payment">
                    <mat-icon>delete_outline</mat-icon>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </ng-container>

    <!-- ===================== RECEIPTS ===================== -->
    <ng-container *ngIf="activeTab === 'RECEIPTS'">
      <div class="table-card">
        <div *ngIf="receipts.length === 0" class="state state--empty">
          <mat-icon>receipt_long</mat-icon>
          <p class="state__title">No receipts yet</p>
          <p class="state__hint">Completed payments and their receipts will appear here.</p>
        </div>

        <div class="table-scroll" *ngIf="receipts.length > 0">
          <table class="grid">
            <thead>
              <tr>
                <th>Confirmation #</th>
                <th>Date</th>
                <th class="num">Amount</th>
                <th>Status</th>
                <th class="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let p of receipts">
                <td data-label="Confirmation #">
                  <a class="link" [routerLink]="['/app/payments/confirmation', p.confirmationNumber]">
                    {{ p.confirmationNumber }}
                  </a>
                </td>
                <td data-label="Date">{{ p.paymentDate | date: 'MM/dd/yyyy' }}</td>
                <td data-label="Amount" class="num amount">{{ p.totalPaid | currency }}</td>
                <td data-label="Status">
                  <span class="status-pill" [ngClass]="getStatusClass(p.status)">
                    <span class="status-pill__dot"></span>{{ p.status }}
                  </span>
                </td>
                <td class="actions-col">
                  <button class="icon-btn icon-btn--view" (click)="viewPaymentDetails(p)" aria-label="View details">
                    <mat-icon>visibility</mat-icon>
                  </button>
                  <button class="icon-btn icon-btn--view" (click)="downloadReceipt(p.id)" aria-label="Download receipt">
                    <mat-icon>download</mat-icon>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </ng-container>

    <!-- Floating basket button -->
    <button class="basket-fab" (click)="goToBasket()" [disabled]="basketCount === 0">
      <mat-icon>shopping_cart</mat-icon>
      ({{ basketCount }}) Cart
    </button>

  </ng-container>
</section>
